import { useCallback, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

type FetchMoreVariables = {
  traceId: string;
  first: number;
  offset: number;
  // Explicitly nulled on every fetch. The initial query and Apollo's fetchMore
  // merge variables over the original call, so any stale cursor/anchor arg left
  // set would combine with `offset` (Postgraphile rejects `offset` + cursor
  // pagination) or skip links past the offset. Sending null omits them.
  after: null;
  before: null;
  last: null;
};

type FetchMoreArg = { variables: FetchMoreVariables };
type FetchMoreFn = (arg: FetchMoreArg) => Promise<unknown>;

export type UseTraceLinksPagination = {
  // Load a self-contained window anchored at an absolute offset (height-DESC
  // index; offset 0 = head). The Trace.links cache policy unions it into the
  // shared window by linkHash sorted height-DESC — non-adjacent windows are
  // fine because the ribbon addresses links by absolute index and renders
  // unloaded indices as skeletons.
  loadRange: (offset: number, first: number) => void;
  loading: boolean;
};

/**
 * Offset-driven pagination over a trace's links.
 *
 * The segment ribbon virtualizes the whole trace by absolute index and maps the
 * visible index range straight to a single offset fetch (see ribbonWindow.ts).
 * There is no cursor bookkeeping and no notion of "older" vs "newer" direction:
 * every fetch is an offset-anchored page, and the cache unions them. An
 * in-flight lock coalesces the burst of range requests a fast scroll produces
 * into one fetch at a time; errors are swallowed to Sentry so a failed page
 * never wedges the loading flag.
 */
export function useTraceLinksPagination(
  fetchMore: FetchMoreFn | undefined,
  traceId: string
): UseTraceLinksPagination {
  const inFlight = useRef(false);
  const [loading, setLoading] = useState(false);

  const loadRange = useCallback(
    (offset: number, first: number) => {
      if (!fetchMore) {
        return;
      }
      if (inFlight.current) {
        return;
      }
      if (offset < 0 || first <= 0) {
        return;
      }

      inFlight.current = true;
      setLoading(true);
      fetchMore({
        variables: {
          traceId,
          first,
          offset,
          after: null,
          before: null,
          last: null
        }
      })
        .catch(err => {
          Sentry.captureException(err);
        })
        .finally(() => {
          inFlight.current = false;
          setLoading(false);
        });
    },
    [fetchMore, traceId]
  );

  return { loadRange, loading };
}
